import { Eye, EyeOff, X } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { Google } from "@/components/icons/google";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { isApiError, useSignup } from "@/lib/api";
import { logger } from "@/lib/logger";

const ALLOWED_DOMAIN = "boardofinnovation.com";

type PasswordStrength = "weak" | "medium" | "strong";

function getPasswordStrength(password: string): PasswordStrength {
  if (password.length < 8) {
    return "weak";
  }

  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);
  const varietyCount = [hasUpper, hasLower, hasNumber, hasSpecial].filter(Boolean).length;

  if (password.length >= 12 && varietyCount >= 3) {
    return "strong";
  }
  if (password.length >= 8 && varietyCount >= 2) {
    return "medium";
  }
  return "weak";
}

const strengthConfig: Record<PasswordStrength, { label: string; color: string; width: string }> = {
  weak: { label: "Weak", color: "bg-red-500", width: "w-1/3" },
  medium: { label: "Medium", color: "bg-yellow-500", width: "w-2/3" },
  strong: { label: "Strong", color: "bg-green-500", width: "w-full" },
};

export function SignupPage() {
  const navigate = useNavigate();
  const signupMutation = useSignup();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [domainWarning, setDomainWarning] = useState<string | null>(null);
  const loading = signupMutation.isPending;

  const strength = password ? getPasswordStrength(password) : null;
  const cfg = strength ? strengthConfig[strength] : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Client-side validation
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    // Domain check
    const domain = email.split("@")[1]?.toLowerCase() ?? "";
    if (domain !== ALLOWED_DOMAIN) {
      setDomainWarning(domain || "unknown");
      return;
    }

    logger.debug("[signup] submitting for", email);
    try {
      await signupMutation.mutateAsync({ email, password, firstName, lastName });
      logger.debug("[signup] success → redirecting to /verify-email");
      navigate("/verify-email");
    } catch (err) {
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      logger.debug("[signup] failed:", err);
      setError(detail ?? "Registration failed. Please try again.");
    }
  };

  const handleGoogleSignup = () => {
    logger.debug("[signup] redirecting to Google SSO");
    window.location.assign("/api/auth/google/login");
  };

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-primary text-2xl font-normal">Scaffold UI</CardTitle>
          <p className="text-muted text-sm">Create your account</p>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleSignup}
            disabled={loading}
            className="w-full"
          >
            <Google className="size-4" />
            Continue with Google
          </Button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <Separator />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-card text-muted px-2 text-xs uppercase">or</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name fields */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label htmlFor="firstName" className="text-foreground text-sm font-medium">
                  First name
                </label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="lastName" className="text-foreground text-sm font-medium">
                  Last name
                </label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label htmlFor="email" className="text-foreground text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="john.doe@boardofinnovation.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label htmlFor="password" className="text-foreground text-sm font-medium">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {/* Password strength indicator */}
              {password && cfg && (
                <div className="space-y-1">
                  <div className="bg-border h-1.5 w-full overflow-hidden rounded-full">
                    <div
                      className={`h-full rounded-full transition-all ${cfg.color} ${cfg.width}`}
                    />
                  </div>
                  <p
                    className={`text-xs ${strength === "weak" ? "text-red-500" : strength === "medium" ? "text-yellow-600" : "text-green-600"}`}
                  >
                    {cfg.label}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-foreground text-sm font-medium">
                Confirm password
              </label>
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={
                  confirmPassword && confirmPassword !== password
                    ? "border-red-500 focus-visible:ring-red-500"
                    : ""
                }
              />
              {confirmPassword && confirmPassword !== password && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}

            <Button
              type="submit"
              className="bg-accent text-accent-foreground hover:bg-accent/90 w-full"
              disabled={loading}
            >
              {loading ? "Creating account..." : "Sign up"}
            </Button>

            <p className="text-muted text-center text-sm">
              Already have an account?{" "}
              <Link to="/login" className="text-accent hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>

      {/* Domain restriction popup */}
      {domainWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card relative mx-4 w-full max-w-sm rounded-xl p-6 shadow-xl">
            <button
              onClick={() => setDomainWarning(null)}
              className="text-muted hover:text-foreground absolute top-3 right-3"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            <h3 className="text-primary mb-2 text-lg font-bold">Domain not supported</h3>
            <p className="text-foreground text-sm">
              The domain <strong>{domainWarning}</strong> is not supported. Only{" "}
              <strong>@{ALLOWED_DOMAIN}</strong> email addresses can sign up.
            </p>
            <p className="text-muted mt-3 text-sm">
              Contact{" "}
              <a
                href="mailto:ruben.broekx@boardofinnovation.com"
                className="text-accent hover:underline"
              >
                ruben.broekx@boardofinnovation.com
              </a>{" "}
              to be granted access.
            </p>
            <Button
              onClick={() => setDomainWarning(null)}
              className="bg-accent text-accent-foreground hover:bg-accent/90 mt-4 w-full"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
